import React, { useState, useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import EntityTable from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ActividadesPlantillaList from '../components/plantillas/ActividadesPlantillaList';
import { useToast } from '../components/contexts/ToastContext';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import useModal from '../hooks/useModal';
import HighlightedText from '../components/common/HighlightedText';
import apiService from '../services/apiService';
import { useExportExcel } from '../hooks/useExportExcel';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Pagina de gestion de plantillas de mantenimiento
 *
 * CRUD de plantillas con sidebar para gestionar actividades.
 * Patron: Equipos.js (CRUD + sidebar padre-hijo)
 *
 * @returns {JSX.Element}
 */
function PlantillasMantenimiento() {
  const { hasPermission } = useAuth();

  const {
    data: plantillas,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem: editingPlantilla,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm,
    refetch
  } = usePaginatedEntity({
    entityName: 'plantilla de mantenimiento',
    entityNamePlural: 'plantillas de mantenimiento',
    fetchFn: apiService.getPlantillasMantenimiento,
    createFn: apiService.createPlantillaMantenimiento,
    updateFn: apiService.updatePlantillaMantenimiento,
    deleteFn: apiService.deletePlantillaMantenimiento,
    defaultFilters: { search: '' },
    filterKeys: ['search'],
    messages: {
      createSuccess: 'Plantilla creada',
      updateSuccess: 'Plantilla actualizada',
      deleteSuccess: 'Plantilla eliminada exitosamente'
    }
  });

  const { showToast } = useToast();

  // Sidebar de actividades
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);

  // useModal registra listener Escape + bloquea scroll del body (overflow: hidden)
  // cuando el sidebar está abierto. Ambos efectos son deseados.
  useModal(!!selectedPlantilla, () => setSelectedPlantilla(null));
  const [actividadModalOpen, setActividadModalOpen] = useState(false);
  const [editingActividad, setEditingActividad] = useState(null);
  const [actividadesRefreshTrigger, setActividadesRefreshTrigger] = useState(0);
  const [actividadLoading, setActividadLoading] = useState(false);
  const [actividadToDelete, setActividadToDelete] = useState(null);

  /**
   * Guarda una actividad (crear o actualizar)
   * @param {Object} data - Datos de la actividad
   */
  const handleSaveActividad = async (data) => {
    setActividadLoading(true);
    try {
      let response;
      if (editingActividad) {
        response = await apiService.updateActividadPlantilla(
          selectedPlantilla.id, editingActividad.id, data
        );
      } else {
        response = await apiService.createActividadPlantilla(selectedPlantilla.id, data);
      }

      if (response.success) {
        showToast(editingActividad ? 'Actividad actualizada' : 'Actividad creada', 'success');
        setActividadesRefreshTrigger(prev => prev + 1);
        refetch();
        setActividadModalOpen(false);
        setEditingActividad(null);
      } else {
        showToast(response.message || 'Error al guardar actividad', 'error');
      }
    } catch (error) {
      showToast('Error al guardar actividad', 'error');
    } finally {
      setActividadLoading(false);
    }
  };

  /**
   * Elimina una actividad de la plantilla
   */
  const handleDeleteActividad = async () => {
    if (!actividadToDelete) return;
    try {
      const response = await apiService.deleteActividadPlantilla(
        selectedPlantilla.id, actividadToDelete.id
      );
      if (response.success) {
        showToast('Actividad eliminada', 'success');
        setActividadesRefreshTrigger(prev => prev + 1);
        refetch();
      } else {
        showToast(response.message || 'Error al eliminar', 'error');
      }
    } catch (error) {
      showToast('Error al eliminar actividad', 'error');
    } finally {
      setActividadToDelete(null);
    }
  };

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (p) => ({ 'Nombre': p.nombre, 'Descripción': p.descripcion || '', 'Actividades': p.cantidad_actividades || 0 }),
    sheetName: 'Plantillas',
    columnWidths: [{ wch: 25 }, { wch: 40 }, { wch: 12 }],
    entityLabel: 'plantillas'
  });

  const columns = useMemo(() => [
    { key: 'nombre', label: 'Nombre', sortable: true, highlightable: true, title: true },
    {
      key: 'descripcion',
      label: 'Descripcion',
      render: (row, searchTerm) => {
        const text = row.descripcion || '-';
        const truncated = text.length > 80 ? text.substring(0, 80) + '...' : text;
        return <HighlightedText text={truncated} searchTerm={searchTerm} />;
      }
    },
    {
      key: 'cantidad_actividades',
      label: 'Actividades',
      mobileBadge: true,
      render: (row) => {
        const count = row.cantidad_actividades || 0;
        return (
          <span
            className={`badge ${count === 0 ? 'badge-warning' : 'badge-success'}`}
            title={count === 0 ? 'Sin actividades — no se puede usar en mantenimientos' : `${count} actividad(es)`}
          >
            {count} act.
          </span>
        );
      }
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  const modalFields = useMemo(() => [
    { name: 'nombre', label: 'Nombre', type: 'text', required: true, maxLength: 150 },
    { name: 'descripcion', label: 'Descripcion', type: 'textarea' }
  ], []);

  const actividadModalFields = useMemo(() => [
    { name: 'nombre', label: 'Nombre', type: 'text', required: true, maxLength: 150 },
    { name: 'descripcion', label: 'Descripción', type: 'textarea' }
  ], []);

  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por nombre o descripcion...',
      icon: 'fa-search'
    }
  ], []);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Plantillas' }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nueva Plantilla', icon: 'fa-plus', variant: 'primary', onClick: handleCreate, show: () => hasPermission('plantillas_mantenimiento.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={plantillas}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron plantillas"
          actions={[
            { icon: 'fa-tasks',   className: 'btn-ghost-info',    title: 'Ver actividades', onClick: (row) => setSelectedPlantilla(row), show: () => hasPermission('plantillas_mantenimiento.read') },
            { icon: 'fa-edit',    className: 'btn-ghost-primary', title: 'Editar',          onClick: handleEdit,                        show: () => hasPermission('plantillas_mantenimiento.update') },
            { icon: 'fa-trash',   className: 'btn-ghost-danger',  title: 'Eliminar',        onClick: handleDelete,                      show: () => hasPermission('plantillas_mantenimiento.delete') },
          ]}
          animateRows={true}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}
          fields={modalFields}
          initialData={editingPlantilla}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Plantilla"
          message={`¿Esta seguro de eliminar la plantilla "${confirmDialog.item?.nombre}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>

      {/* Sidebar de Actividades */}
      {selectedPlantilla && (
        <>
          <div
            className="sidebar-overlay"
            onClick={() => setSelectedPlantilla(null)}
          />
          <div className="sidebar-responsive-lg active bg-sidebar">
            <div className="sidebar-header">
              <div className="sidebar-header-content">
                <h3 className="sidebar-title">
                  <i className="fas fa-tasks"></i> {selectedPlantilla.nombre}
                </h3>
                <p className="sidebar-subtitle">Protocolo de actividades</p>
              </div>
              <button
                className="sidebar-close"
                onClick={() => setSelectedPlantilla(null)}
                aria-label="Cerrar panel de actividades"
                title="Cerrar"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <ActividadesPlantillaList
              plantillaId={selectedPlantilla.id}
              onAddClick={() => {
                setEditingActividad(null);
                setActividadModalOpen(true);
              }}
              onEditClick={(actividad) => {
                setEditingActividad(actividad);
                setActividadModalOpen(true);
              }}
              onDeleteRequest={(actividad) => setActividadToDelete(actividad)}
              refreshTrigger={actividadesRefreshTrigger}
            />
          </div>
        </>
      )}

      {/* Modal de actividad */}
      <EntityModal
        isOpen={actividadModalOpen}
        onClose={() => {
          setActividadModalOpen(false);
          setEditingActividad(null);
        }}
        onSave={handleSaveActividad}
        title={editingActividad ? 'Editar Actividad' : 'Nueva Actividad'}
        fields={actividadModalFields}
        initialData={editingActividad}
        loading={actividadLoading}
      />

      {/* Confirmacion eliminar actividad */}
      <ConfirmDialog
        isOpen={!!actividadToDelete}
        onCancel={() => setActividadToDelete(null)}
        onConfirm={handleDeleteActividad}
        title="Eliminar Actividad"
        message={`¿Esta seguro de eliminar la actividad "${actividadToDelete?.nombre}"?`}
        confirmText="Eliminar"
        variant="danger"
      />
    </>
  );
}

export default PlantillasMantenimiento;
